import { RiskRegisterBulkDialogType } from 'src/app/enums/riskRegisterBulkDialogType.enum';
import {
    RiskRegisterBulkEdit,
    RiskRegisterBulkFailure,
    RiskRegisterBulkSummary,
    RiskRegisterBulkTarget,
    RiskRegisterBulkSummaryContext,
    RiskRegisterBulkUpdateRequest,
    RiskRegisterBulkUpdateResponse,
    RiskRegisterUpdateFields,
    RiskRegisterUpdateResult,
} from 'src/app/models/risk-register-bulk-update.model';
import { RiskRegisterAgentColumnDeleteResponse, RiskRegisterDeleteFailure } from 'src/app/models/risk-register-seg-page.model';

const HEALTH_RISK_SCORE_FIELD = 'HealthRiskScore';

function buildUpdateFields(edit: RiskRegisterBulkEdit): RiskRegisterUpdateFields {
    switch (edit.type) {
        case RiskRegisterBulkDialogType.AssessmentStatus:
            return { assessmentStatus: edit.value };
        case RiskRegisterBulkDialogType.MethodOfAssessment:
            return { methodOfAssessment: edit.value };
        case RiskRegisterBulkDialogType.HealthRiskScore:
            return {
                ...(edit.value !== undefined && { healthRiskScore: { value: edit.value } }),
                ...(edit.assessmentStatus != null && { assessmentStatus: edit.assessmentStatus }),
            };
        case RiskRegisterBulkDialogType.ConsequenceRating:
            return { consequenceRating: edit.value, consequenceRatingComment: edit.comment.trim() };
    }
}

export function buildBulkUpdateRequest(target: RiskRegisterBulkTarget, edit: RiskRegisterBulkEdit): RiskRegisterBulkUpdateRequest {
    return { ...target, riskRegisterUpdateFields: buildUpdateFields(edit) };
}

export function mergeBulkUpdateResponses(
    first: RiskRegisterBulkUpdateResponse,
    second: RiskRegisterBulkUpdateResponse,
): RiskRegisterBulkUpdateResponse {
    return {
        totalRequested: first.totalRequested + second.totalRequested,
        successCount: first.successCount + second.successCount,
        results: [...(first.results ?? []), ...(second.results ?? [])],
    };
}

function resolveResultLabel(result: RiskRegisterUpdateResult, unknownLabel: string): string {
    const parts = [result.segName, result.agentName].filter((part) => !!part);
    return parts.length ? parts.join(' / ') : unknownLabel;
}

function isScoreIgnored(result: RiskRegisterUpdateResult): boolean {
    const appliedFields = result.updatedFields?.generalFields;
    return result.success && Array.isArray(appliedFields) && !appliedFields.includes(HEALTH_RISK_SCORE_FIELD);
}

function resolveSegLabel(failure: RiskRegisterDeleteFailure, unknownLabel: string): string {
    if (!failure.segName) {
        return unknownLabel;
    }
    return failure.segCode ? `${failure.segName} (${failure.segCode})` : failure.segName;
}

export function summarizeColumnDelete(
    response: RiskRegisterAgentColumnDeleteResponse,
    locale: string,
    unknownLabel: string,
): RiskRegisterBulkSummary {
    const rejected = response.failures ?? [];
    const failures: RiskRegisterBulkFailure[] = rejected
        .map((failure) => ({ label: resolveSegLabel(failure, unknownLabel), reason: failure.errorMessage }))
        .sort((a, b) => a.label.localeCompare(b.label, locale));

    return {
        total: response.totalRequested,
        updated: response.deletedCount,
        failed: failures.length,
        failedIds: rejected.map((failure) => failure.riskRegisterId),
        failures,
    };
}

export function summarizeBulkUpdate(
    response: RiskRegisterBulkUpdateResponse,
    context: RiskRegisterBulkSummaryContext,
): RiskRegisterBulkSummary {
    const results = response.results ?? [];
    const rejected = results.filter((result) => !result.success);
    const notChanged = context.expectsHealthRiskScore ? results.filter(isScoreIgnored) : [];
    const reportedIds = new Set(results.map((result) => result.riskRegisterId));
    const missingIds = context.sentIds.filter((id) => !reportedIds.has(id));

    const failures: RiskRegisterBulkFailure[] = [
        ...rejected.map((result) => ({ label: resolveResultLabel(result, context.unknownLabel), reason: result.errorMessage })),
        ...notChanged.map((result) => ({ label: resolveResultLabel(result, context.unknownLabel), reason: context.notChangedReason })),
        ...missingIds.map(() => ({ label: context.unknownLabel, reason: null })),
    ].sort((a, b) => a.label.localeCompare(b.label, context.locale));

    const updated = Math.max(0, response.successCount - notChanged.length);

    return {
        total: response.totalRequested,
        updated,
        failed: Math.max(0, response.totalRequested - updated),
        failedIds: [...rejected, ...notChanged].map((result) => result.riskRegisterId).concat(missingIds),
        failures,
    };
}
